import { STOREFRONT_VALUES_KEYS } from "@/constants/storefront_values";
import { Observable, from, share, tap } from "rxjs";
import { Struct, create } from "superstruct";
import { singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import type {
    GrowaveApiResponseBody,
    SelectValueParams,
    Strategy,
} from "../types";

@singleton()
class StorefrontValuesManager {
    private readonly cache: Map<string, Observable<unknown>> = new Map();
    private readonly strategies: Array<Strategy> = [];

    constructor(private readonly logger: LoggerInterface) {}

    public registerStrategies(strategies: Array<Strategy>): void {
        this.strategies.push(...strategies);
    }

    public selectValue<T>({
        namespace,
        key,
        schema,
    }: SelectValueParams<T>): Observable<T> {
        const cacheKey = `${namespace}:${key}`;
        let storefrontValues$ = this.cache.get(cacheKey);

        if (!storefrontValues$) {
            storefrontValues$ = from(
                this.getAndValidateValueFromStrategy({ namespace, key, schema })
            ).pipe(
                tap((value) =>
                    this.logger.debug(
                        "StorefrontValuesManager.selectValue",
                        value
                    )
                ),
                share()
            );
            this.cache.set(cacheKey, storefrontValues$);
        }

        return storefrontValues$ as Observable<T>;
    }

    private async getAndValidateValueFromStrategy<T>({
        namespace,
        key,
        schema,
    }: SelectValueParams<T>): Promise<T> {
        for (const strategy of this.strategies) {
            const value = await strategy.getValue({ namespace, key });

            if (value) {
                try {
                    return this.validate({
                        value,
                        schema,
                        key,
                    });
                } catch (error) {
                    this.logger.error(
                        `Validation failed for key: ${key}`,
                        error
                    );
                }
            }
        }
        throw new Error(`Value not found for key: ${key}`);
    }

    private validate<T>({
        value,
        schema,
        key,
    }: {
        value: unknown | Array<GrowaveApiResponseBody>;
        schema: Struct<T>;
        key: STOREFRONT_VALUES_KEYS;
    }): T {
        if (Array.isArray(value)) {
            const valueFromArray = value.find(
                (
                    item: GrowaveApiResponseBody
                ): item is GrowaveApiResponseBody => item.key === key
            )?.value;

            return create(valueFromArray, schema);
        }

        return create(value, schema);
    }
}

export default StorefrontValuesManager;
